// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';

const ANIM_CSS = `
@keyframes pf-pulse {
  0%, 100% { opacity: 1;    transform: scale(1)    rotate(0deg);  }
  50%       { opacity: 0.75; transform: scale(1.15) rotate(15deg); }
}
@keyframes pf-spin {
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
}
@keyframes pf-stage-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0);   }
}
@keyframes pf-banner-out {
  to { opacity: 0; transform: translateY(-100%); }
}
`;

// Stage determination based on job state
function getStage(jobStatus, pagesScanned, pageCount, productCount) {
  if (jobStatus === 'complete') return 4;
  if (jobStatus === 'failed')   return -1;
  if (!jobStatus || jobStatus === 'queued') return 0;
  if (productCount > 0) return 2;
  if (pagesScanned > 0) return 1;
  return 1;
}

function isFinalizingStage(jobStatus) {
  return jobStatus === 'complete';
}

export default function ProcessingFeed({
  jobStatus,
  pagesScanned   = 0,
  pageCount      = 0,
  productCount   = 0,
  queuePosition  = 0,
  readyCount     = 0,
  flaggedCount   = 0,
  pct            = 0,
  complete,
  jobStartedAt   = null,
}) {
  const prefersReduced = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // Delay "all done" state by 400ms so the banner can briefly show before animating out
  const [finalDone, setFinalDone] = useState(false);
  useEffect(() => {
    if (jobStatus === 'complete' || complete) {
      const t = setTimeout(() => setFinalDone(true), 400);
      return () => clearTimeout(t);
    } else {
      setFinalDone(false);
    }
  }, [jobStatus, complete]);

  const stage        = getStage(jobStatus, pagesScanned, pageCount, productCount);
  const isFinalizing = isFinalizingStage(jobStatus);
  const isComplete   = finalDone;
  const isFailed     = stage === -1;
  const isQueued     = stage === 0;

  // Progress bar: deterministic when we have page scan data or products, indeterminate only when no signals
  const hasPageProgress = pageCount > 0 && pagesScanned > 0;
  const isIndeterminate = stage === 1 && !hasPageProgress && !isFinalizing && !isComplete;

  // Progress value: use page scanning during stage 1, server pct otherwise
  const progressPct = (() => {
    if (isComplete) return 100;
    if (stage === 1 && hasPageProgress) {
      // Pages scanned gives us 5–70% of the bar; remaining 30% is for product extraction + finalization
      return Math.max(2, Math.min(70, (pagesScanned / pageCount) * 70));
    }
    return Math.max(2, Math.min(95, pct));
  })();

  if (isFailed) return null;

  // Banner copy — stage-driven
  let bannerText;
  if (isQueued) {
    bannerText = queuePosition === 1
      ? 'Starting now…'
      : 'Preparing your catalog…';
  } else if (productCount > 0 && flaggedCount > 0) {
    bannerText = `${readyCount} ready · ${flaggedCount} need review`;
  } else if (productCount > 0) {
    bannerText = `Found ${productCount} products so far`;
  } else if (isFinalizing || pct >= 80) {
    bannerText = `Finalizing ${productCount > 0 ? productCount + ' ' : ''}products…`;
  } else if (hasPageProgress) {
    bannerText = `Reading your catalog… page ${pagesScanned} of ${pageCount}`;
  } else if (pageCount > 0) {
    bannerText = `Reading your ${pageCount}-page catalog…`;
  } else {
    bannerText = 'Reading your catalog…';
  }

  // Secondary text — shown during active processing (not queued, not complete)
  const showSecondary = stage >= 1 && stage < 4 && !isComplete;

  return (
    <>
      <style>{ANIM_CSS}</style>

      {/* ── Sticky processing banner ─────────────────────────────────────── */}
      <div
        aria-live="polite"
        aria-label="Catalog processing progress"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          height: 36,
          background: 'var(--color-primary)',
          zIndex: 60,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 20,
          paddingRight: 20,
          gap: 8,
          animation: isComplete ? 'pf-banner-out 300ms ease-in forwards' : undefined,
        }}
      >
        {/* Dual-attention icon: outer arc (fast) + inner star (slow) */}
        <div style={{ position: 'relative', width: 13, height: 13, flexShrink: 0 }}>
          <svg width="13" height="13" viewBox="0 0 13 13" style={{ position: 'absolute', inset: 0, animation: prefersReduced ? undefined : 'pf-spin 1.6s linear infinite' }}>
            <circle cx="6.5" cy="6.5" r="5" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2"/>
            <circle cx="6.5" cy="6.5" r="5" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeDasharray="14 18"/>
          </svg>
          <svg width="13" height="13" viewBox="0 0 13 13" style={{ position: 'absolute', inset: 0, animation: prefersReduced ? undefined : 'pf-pulse 2.4s ease-in-out infinite' }}>
            <path d="M6.5 4l.6 1.8L9 6.5l-1.9.7L6.5 9l-.6-1.8L4 6.5l1.9-.7z" fill="rgba(255,255,255,0.5)"/>
          </svg>
        </div>

        {/* Main copy + secondary hint */}
        <span
          key={bannerText}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'baseline',
            gap: 0,
            animation: prefersReduced ? undefined : 'pf-stage-in 300ms ease forwards',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
            {bannerText}
          </span>
          {showSecondary && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginLeft: 8, flexShrink: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Safe to close — we'll email you when ready
            </span>
          )}
        </span>

        {/* Progress bar at bottom edge */}
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: 4,
          background: 'rgba(255,255,255,0.30)',
          overflow: 'hidden',
        }}>
          {isIndeterminate ? (
            /* Indeterminate shimmer — only when no page count data at all */
            <div style={{
              position: 'absolute',
              top: 0,
              height: '100%',
              background: 'rgba(255,255,255,1)',
              borderRadius: 2,
              animation: prefersReduced ? undefined : 'pf-indeterminate 2.4s ease-in-out infinite',
            }} />
          ) : (
            /* Deterministic fill — page progress in stage 1, server pct in stages 2+ */
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              background: 'rgba(255,255,255,1)',
              borderRadius: 2,
              transition: 'width 0.5s ease',
            }} />
          )}
        </div>
      </div>
    </>
  );
}
